import { Card, Grid, TextField, Typography } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    useGetListDataQuery,
    useUpdateListDataMutation,
} from '../../api/apiSlice';
import { useUpdateUserMutation } from '../../api/authApiSlice';
import FormikButton from '../../components/formik/FormikButton';
import FormikInput from '../../components/formik/FormikInput';
import FormikSelect from '../../components/formik/FormikSelect';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import Form from '../../components/utilities/Form';
import {
    educationInputs,
    maritalInputs,
    optionInputs,
    salvationInputs,
} from '../../enums';
import useAuth from '../../hooks/useAuth';
import { setUser } from '../../store/slices/authSlice';
import errorSnackbar from '../../utils/errorSnackbar';

export default function ScreenGuestProfileUpdate() {
    const [search, setSearch] = useState('');
    const [resultLocation, setResultLocation] = useState(null);
    const [locationSelected, setLocationSelected] = useState(false);
    const { user } = useAuth();

    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    const [updateUser, { isLoading }] = useUpdateUserMutation();
    const [updatePastor, { isLoading: isLoadingPastor }] =
        useUpdateListDataMutation();
    const { data: searchPlacesData } = useGetListDataQuery(
        { list: 'church/finder', params: { q: search } },
        { refetchOnMountOrArgChange: true }
    );
    const { data: ministerData } = useGetListDataQuery(
        { list: `pastor/${user.username}` },
        { refetchOnMountOrArgChange: true }
    );

    const searchPlaces = searchPlacesData?.data;
    const { about } = ministerData?.data || {};

    const [edBio, setEdBio] = useState(about);

    const {
        firstName,
        surname,
        gender,
        date_of_birth,
        estate,
        contact,
        status,
    } = user || {};

    const { phoneNumber, email } = contact || {};

    const {
        maritalStatus,
        baptised,
        confirmed,
        salvation,
        occupation,
        education,
    } = status || {};

    useEffect(() => {
        setEdBio(about);
    }, [about]);

    return (
        <ExternalScreen title="Update Profile">
            <Grid item xs={12}>
                <Typography sx={{ mb: 2 }} variant="h6">
                    Basic Profile
                </Typography>
                <Form
                    onSubmit={async (vals) => {
                        const { error, data } = await updateUser({
                            body: {
                                ...vals,
                                phoneNumber,
                                gender,
                                estate,
                                dob: vals.dob?.replaceAll('-', '/'),
                            },
                        });

                        errorSnackbar(error, enqueueSnackbar);

                        if (data) {
                            dispatch(setUser(data?.data));
                            enqueueSnackbar('Profile Updated', {
                                variant: 'success',
                            });
                        }
                    }}
                    initialValues={{
                        firstName,
                        surname,
                        email,
                        dob: date_of_birth,
                    }}
                >
                    <FormikInput
                        name="firstName"
                        label="First Name"
                        placeholder={firstName}
                    />
                    <FormikInput
                        name="surname"
                        label="Last Name"
                        placeholder={surname}
                    />
                    <FormikInput
                        name="email"
                        label="Email Address"
                        placeholder={email}
                    />
                    <FormikInput
                        type="date"
                        name="dob"
                        label="DOB"
                        placeholder={date_of_birth}
                    />
                    <div className="d-flex align-items-center justify-content-between">
                        <div />
                        <FormikButton loading={isLoading} variant="contained">
                            Update
                        </FormikButton>
                    </div>
                </Form>
                <Typography sx={{ my: 2 }} variant="h6">
                    Other Info
                </Typography>
                <Form
                    onSubmit={async (vals) => {
                        const { error, data } = await updateUser({
                            body: {
                                ...vals,
                                phoneNumber,
                                gender,
                                email,
                                firstName,
                                surname,
                                estate,
                            },
                            type: 'status',
                        });

                        errorSnackbar(error, enqueueSnackbar);

                        if (data) {
                            dispatch(setUser(data?.data));
                            enqueueSnackbar('Profile Updated', {
                                variant: 'success',
                            });
                        }
                    }}
                    initialValues={{
                        maritalStatus: maritalStatus,
                        baptised: baptised,
                        confirmed: confirmed,
                        salvation,
                        occupation,
                        education,
                    }}
                >
                    <FormikSelect
                        name="maritalStatus"
                        label="Marital Status"
                        data={maritalInputs}
                    />
                    <FormikSelect
                        name="baptised"
                        label="Baptised"
                        data={optionInputs}
                    />
                    <FormikSelect
                        name="confirmed"
                        label="Confirmed"
                        data={optionInputs}
                    />
                    <FormikSelect
                        name="salvation"
                        label="Salvation"
                        data={salvationInputs}
                    />
                    <FormikSelect
                        name="education"
                        label="Education Level"
                        data={educationInputs}
                    />
                    <FormikInput
                        name="occupation"
                        label="Occupation"
                        placeholder={occupation}
                    />
                    <div className="d-flex align-items-center justify-content-between">
                        <div />
                        <FormikButton loading={isLoading} variant="contained">
                            Update
                        </FormikButton>
                    </div>
                </Form>
                <Typography sx={{ my: 2 }} variant="h6">
                    Pastor&apos;s Info
                </Typography>
                <Form
                    onSubmit={async () => {
                        if (!locationSelected) {
                            enqueueSnackbar('Please select a location', {
                                variant: 'error',
                            });
                            return;
                        }

                        const { error, data } = await updatePastor({
                            list: '/op/pastors',
                            body: {
                                lat: resultLocation?.lat,
                                long: resultLocation?.lng,
                                bio: edBio,
                            },
                        });

                        errorSnackbar(error, enqueueSnackbar);

                        if (data) enqueueSnackbar(data?.message);
                    }}
                    initialValues={{}}
                >
                    <Typography sx={{ mx: 1, mb: 0.5 }}>Bio</Typography>
                    <Editor
                        apiKey="wk87szosn5rjqu90ub5t313n25a9gx9x6dnfaav2sdoobb88"
                        onEditorChange={(html) => setEdBio(html)}
                        value={edBio}
                        cloudChannel="6-dev"
                        init={{}}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Your Location"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ my: 2 }}
                    />
                    {searchPlaces?.map(({ address, location }) => (
                        <Grid
                            item
                            xs={12}
                            key={address}
                            onClick={() => {
                                setLocationSelected(true);
                                setSearch(address);
                                setResultLocation({
                                    lat: Number(location?.latitude),
                                    lng: Number(location?.longitude),
                                });
                            }}
                        >
                            <Card
                                sx={{ p: 1.5, mx: 0.8, my: 0.2 }}
                                variant="outlined"
                            >
                                <Typography variant="h6">{address}</Typography>
                            </Card>
                        </Grid>
                    ))}

                    <div className="d-flex align-items-center justify-content-between">
                        <div />
                        <FormikButton
                            loading={isLoadingPastor}
                            variant="contained"
                        >
                            Update
                        </FormikButton>
                    </div>
                </Form>
            </Grid>
        </ExternalScreen>
    );
}
